import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  AlertTriangle,
  Camera,
  X,
  Save,
  Edit3,
  User,
  Wifi,
  WifiOff,
  Loader2,
  Phone,
  Hash,
  MapPin,
  Store,
  Tablet,
  UserCheck,
  KeyRound,
  Eye,
  EyeOff,
  Trash2,
} from "lucide-react";
import ImageCropModal from "../ImageCropModal";
import ImageLightbox from "../ImageLightbox";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLOTS = [
  { key: "personal_photo", label: "صورة شخصية", color: "#3B82F6" },
  { key: "id_front", label: "وجه الهوية", color: "#7ED321" },
  { key: "id_back", label: "ظهر الهوية", color: "#F5A623" },
  { key: "ipad_doc", label: "وثيقة الايباد", color: "#E85D45" },
  { key: "market_photo", label: "صورة الماركت", color: "#9F7AEA" },
];

const relativeFromNow = (iso) => {
  if (!iso) return "غير معروف";
  const t = new Date(iso).getTime();
  if (!t) return "غير معروف";
  const diff = Math.max(0, Date.now() - t);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "الآن";
  if (mins < 60) return `قبل ${mins} دقيقة`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `قبل ${hrs} ساعة`;
  const days = Math.floor(hrs / 24);
  return `قبل ${days} يوم`;
};

const CustomerDocCard = ({ doc, onRefresh }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    customer_name: doc.customer_name || "",
    market_name: doc.market_name || "",
    address: doc.address || "",
    customer_id: doc.customer_id || "",
    phone: doc.phone || "",
  });
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [pendingFile, setPendingFile] = useState(null); // {slot, file}
  const [savingSlot, setSavingSlot] = useState(null);
  const [lightboxSlot, setLightboxSlot] = useState(null); // SLOTS entry being viewed

  const handleFilePick = (slot, file) => {
    if (!file) return;
    setPendingFile({ slot, file });
  };

  const uploadCropped = async (croppedFile) => {
    if (!pendingFile) return;
    const slot = pendingFile.slot;
    setSavingSlot(slot);
    try {
      const fd = new FormData();
      fd.append("file", croppedFile);
      const { data } = await axios.post(`${API}/admin/upload-image`, fd);
      await axios.patch(`${API}/admin/customer-docs/${doc.id}`, { [slot]: data.path });
      toast.success(`تم رفع ${SLOTS.find((s) => s.key === slot)?.label}`);
      setPendingFile(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل رفع الصورة");
    } finally {
      setSavingSlot(null);
    }
  };

  const clearSlot = async (slot) => {
    if (!(await confirmDialog("حذف هذه الصورة؟"))) return;
    try {
      await axios.delete(`${API}/admin/customer-docs/${doc.id}/${slot}`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل الحذف");
    }
  };

  // 🗑️ Delete the ENTIRE documentation record for this customer (all
  // images + notes). Two-step confirm to prevent accidents. Doesn't
  // delete the customer profile itself — only the doc content.
  const deleteAllDocs = async () => {
    const ok = await confirmDialog(
      `🗑️ حذف كامل توثيق الزبون "${doc.customer_name || doc.market_name || doc.phone}"؟\n\n` +
      `• سيتم حذف جميع الصور المرفقة (شخصية، بطاقة، آيباد، ماركت).\n` +
      `• سيتم مسح ملاحظات التوثيق.\n` +
      `• الزبون نفسه لن يُحذف — فقط ملف توثيقه.\n\n` +
      `⚠️ هذا الإجراء لا يمكن التراجع عنه.`
    );
    if (!ok) return;
    try {
      await axios.delete(`${API}/admin/customer-docs/${doc.id}`);
      toast.success("تم حذف كامل توثيق الزبون");
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل الحذف");
    }
  };

  // Toggle the customer's documentation type. Clicking the same badge again
  // clears the tag. Used to designate whether a customer (a) participates in
  // admin profit reporting (iPad) or (b) is a walk-in/cash-only sale that
  // must be excluded from profit totals (Regular).
  const setCustomerType = async (newType) => {
    const next = doc.customer_type === newType ? null : newType;
    try {
      await axios.patch(`${API}/admin/customer-docs/${doc.id}`, { customer_type: next });
      toast.success(next === "ipad" ? "تم تحديد: زبون ايباد" : next === "regular" ? "تم تحديد: زبون عادي (لا يُحسب بالأرباح)" : "أزيل التصنيف");
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل التحديث");
    }
  };

  // Switch the customer's tier (A+ / B+). A+ customers see retail prices,
  // B+ customers see wholesale prices and pay the +50 IQD loose-piece
  // surcharge. Each click flips between A+ and B+.
  const setCustomerTier = async (newTier) => {
    if (doc.customer_tier === newTier) return;
    try {
      await axios.patch(`${API}/admin/customer-docs/${doc.id}`, { customer_tier: newTier });
      const label = newTier === "A_plus" ? "A+ (عادي)" : newTier === "C_plus" ? "C+ (وسط)" : "B+ (جملة)";
      toast.success(`تم التحديث: ${label}`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل التحديث");
    }
  };

  const saveText = async () => {
    try {
      const body = { ...form };
      if (newPassword.trim()) {
        if (newPassword.trim().length < 6) {
          toast.error("كلمة السر يجب أن تكون 6 أحرف على الأقل");
          return;
        }
        body.password = newPassword.trim();
      }
      await axios.patch(`${API}/admin/customer-docs/${doc.id}`, body);
      toast.success(body.password ? "تم الحفظ — كلمة السر الجديدة فعّالة الآن" : "تم الحفظ");
      setEditing(false);
      setNewPassword("");
      setShowPassword(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل الحفظ");
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid={`cust-doc-card-${doc.id}`}
        className={`bg-white rounded-3xl p-4 border-2 shadow-lg ${
          doc.needs_attention ? "border-[#E85D45]" : "border-[#E4EBE6]"
        }`}
      >
        {/* Status row: name pill + online dot */}
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div
            className="flex items-center gap-2 bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white px-3 py-1.5 rounded-full shadow-sm max-w-full"
          >
            <User className="w-4 h-4 shrink-0" />
            <span className="font-bold text-sm truncate" data-testid={`cust-name-${doc.id}`}>
              {doc.customer_name || "بدون اسم"}
            </span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span
              data-testid={`cust-online-dot-${doc.id}`}
              className={`w-3 h-3 rounded-full ${
                doc.is_online
                  ? "bg-[#22C55E] ring-2 ring-[#22C55E]/30 animate-pulse"
                  : "bg-[#9CA3AF]"
              }`}
            />
            <span className="text-[10px] text-[#5C6E64]">
              {doc.is_online ? "متصل الآن" : relativeFromNow(doc.last_seen)}
            </span>
            {/* 🗑️ Trash button — deletes ALL documentation with confirmation */}
            <button
              type="button"
              onClick={deleteAllDocs}
              data-testid={`cust-doc-delete-${doc.id}`}
              title="حذف كامل توثيق الزبون"
              className="w-8 h-8 rounded-full bg-red-50 hover:bg-red-500 hover:text-white text-red-600 border-2 border-red-200 hover:border-red-500 flex items-center justify-center transition-all shadow-sm"
            >
              <Trash2 className="w-4 h-4" strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {/* Customer-type designation — two toggle badges.
              • iPad   (green)        → counted in admin profit reports
              • زبون   (royal blue)   → EXCLUDED from profit reports
            Clicking the active badge again clears the tag. */}
        <div className="flex items-center gap-2 mb-3" data-testid={`cust-type-row-${doc.id}`}>
          <span className="text-[11px] font-bold text-[#5C6E64]">تصنيف:</span>
          <button
            type="button"
            onClick={() => setCustomerType("ipad")}
            data-testid={`cust-type-ipad-${doc.id}`}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-black border-2 transition-all ${
              doc.customer_type === "ipad"
                ? "text-white shadow scale-105"
                : "bg-white text-[#15803D]"
            }`}
            style={{
              background: doc.customer_type === "ipad" ? "linear-gradient(135deg, #15803D 0%, #22C55E 100%)" : undefined,
              borderColor: "#22C55E",
            }}
          >
            <Tablet className="w-3.5 h-3.5" />
            ايباد
          </button>
          <button
            type="button"
            onClick={() => setCustomerType("regular")}
            data-testid={`cust-type-regular-${doc.id}`}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-black border-2 transition-all ${
              doc.customer_type === "regular"
                ? "text-white shadow scale-105"
                : "bg-white text-[#1E3A5F]"
            }`}
            style={{
              background: doc.customer_type === "regular" ? "linear-gradient(135deg, #1E3A5F 0%, #1D4ED8 100%)" : undefined,
              borderColor: "#1D4ED8",
            }}
          >
            <UserCheck className="w-3.5 h-3.5" />
            زبون
          </button>
          {doc.customer_type === "regular" && (
            <span className="text-[9px] text-[#5C6E64] italic" title="هذا الزبون لا يُحسب في تقارير الأرباح">
              (مستثنى من الأرباح)
            </span>
          )}
        </div>

        {/* Tier row — A+ / B+. A+ sees retail prices, B+ sees wholesale +
            pays the loose-piece surcharge. Persist via PATCH endpoint. */}
        <div className="flex items-center gap-2 mb-3" data-testid={`cust-tier-row-${doc.id}`}>
          <span className="text-[11px] font-bold text-[#5C6E64]">المرتبة:</span>
          <button
            type="button"
            onClick={() => setCustomerTier("A_plus")}
            data-testid={`cust-tier-aplus-${doc.id}`}
            className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-black border-2 transition-all ${
              (doc.customer_tier || "A_plus") === "A_plus"
                ? "text-white shadow scale-105"
                : "bg-white text-[#7C3AED]"
            }`}
            style={{
              background: (doc.customer_tier || "A_plus") === "A_plus" ? "linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)" : undefined,
              borderColor: "#A855F7",
            }}
          >
            A+
          </button>
          <button
            type="button"
            onClick={() => setCustomerTier("C_plus")}
            data-testid={`cust-tier-cplus-${doc.id}`}
            className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-black border-2 transition-all ${
              doc.customer_tier === "C_plus"
                ? "text-white shadow scale-105"
                : "bg-white text-[#0891B2]"
            }`}
            style={{
              background: doc.customer_tier === "C_plus" ? "linear-gradient(135deg, #0E7490 0%, #06B6D4 100%)" : undefined,
              borderColor: "#0891B2",
            }}
            title="C+ — وسط بين العادي والجملة (75 د.ع/قطعة)"
          >
            ✦ C+
          </button>
          <button
            type="button"
            onClick={() => setCustomerTier("B_plus")}
            data-testid={`cust-tier-bplus-${doc.id}`}
            className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-black border-2 transition-all ${
              doc.customer_tier === "B_plus"
                ? "text-white shadow scale-105"
                : "bg-white text-[#F59E0B]"
            }`}
            style={{
              background: doc.customer_tier === "B_plus" ? "linear-gradient(135deg, #D97706 0%, #F59E0B 100%)" : undefined,
              borderColor: "#F59E0B",
            }}
          >
            B+
          </button>
        </div>

        {/* BIG image grid — 5 large clear images */}
        <div className="grid grid-cols-5 gap-2 mb-3">
          {SLOTS.map((s) => {
            const img = doc.documents?.[s.key];
            return (
              <div key={s.key} className="relative">
                {img ? (
                  // Has image — click opens lightbox with WhatsApp share
                  <button
                    type="button"
                    onClick={() => setLightboxSlot(s)}
                    data-testid={`cust-slot-${doc.id}-${s.key}`}
                    title={`عرض ${s.label}`}
                    className="block w-full aspect-square rounded-xl overflow-hidden border-2 hover:scale-[1.03] transition-transform shadow-sm"
                    style={{ borderColor: s.color }}
                  >
                    <img
                      src={`${API}/files/${img}`}
                      alt={s.label}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ) : (
                  // Empty slot — clicking picks a file
                  <label
                    data-testid={`cust-slot-${doc.id}-${s.key}`}
                    className="block aspect-square rounded-xl bg-[#F3F6F4] border-2 border-dashed flex flex-col items-center justify-center cursor-pointer hover:bg-[#F0F9FF] overflow-hidden relative"
                    style={{ borderColor: "#BAE6FD" }}
                    title={s.label}
                  >
                    <Camera className="w-5 h-5" style={{ color: s.color }} />
                    <span
                      className="text-[9px] font-bold text-center px-0.5 leading-tight mt-0.5"
                      style={{ color: s.color }}
                    >
                      {s.label}
                    </span>
                    {savingSlot === s.key && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <Loader2 className="w-5 h-5 text-white animate-spin" />
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        handleFilePick(s.key, e.target.files?.[0]);
                        e.target.value = "";
                      }}
                    />
                  </label>
                )}
                {img && (
                  <button
                    onClick={() => clearSlot(s.key)}
                    data-testid={`cust-slot-clear-${doc.id}-${s.key}`}
                    className="absolute -top-1.5 -right-1.5 bg-[#E85D45] hover:bg-[#c14a36] text-white w-5 h-5 rounded-full flex items-center justify-center shadow-md"
                    title="حذف الصورة"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Compact info pills — all together */}
        {editing ? (
          <div className="space-y-2 bg-[#F3F6F4] rounded-xl p-2">
            <input
              value={form.customer_name}
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              data-testid={`cust-name-input-${doc.id}`}
              placeholder="اسم الزبون"
              className="w-full px-3 py-1.5 rounded-lg border border-[#E4EBE6] text-sm bg-white"
            />
            <input
              value={form.market_name}
              onChange={(e) => setForm({ ...form, market_name: e.target.value })}
              data-testid={`cust-market-input-${doc.id}`}
              placeholder="اسم الماركت"
              className="w-full px-3 py-1.5 rounded-lg border border-[#E4EBE6] text-sm bg-white"
            />
            <input
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              data-testid={`cust-address-input-${doc.id}`}
              placeholder="عنوان السكن"
              className="w-full px-3 py-1.5 rounded-lg border border-[#E4EBE6] text-sm bg-white"
            />
            {/* Phone — admin can correct/migrate the customer's login number.
                Server-side rejects collisions with another account's phone. */}
            <input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^0-9+]/g, "") })}
              data-testid={`cust-phone-input-${doc.id}`}
              placeholder="رقم الهاتف"
              inputMode="tel"
              dir="ltr"
              className="w-full px-3 py-1.5 rounded-lg border border-[#E4EBE6] text-sm bg-white tabular-nums"
            />
            {/* iPad number — admin can fix typos or move to a new device.
                Numeric only, 1..999, unique. Empty = no iPad. */}
            <div className="relative">
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-[#1E3A5F] bg-[#3B82F6]/15 rounded-full px-1.5 py-0.5 flex items-center gap-0.5">
                <Tablet className="w-2.5 h-2.5" />
                ايباد
              </span>
              <input
                value={form.customer_id}
                onChange={(e) => setForm({ ...form, customer_id: e.target.value.replace(/[^0-9]/g, "") })}
                data-testid={`cust-ipad-input-${doc.id}`}
                placeholder="رقم الايباد (1-999) — اتركه فارغ بلا ايباد"
                inputMode="numeric"
                maxLength={3}
                className="w-full px-3 py-1.5 pr-16 rounded-lg border border-[#E4EBE6] text-sm bg-white tabular-nums"
                dir="ltr"
              />
            </div>
            {/* New password (admin reset) — leave empty to keep current.
                Visible only in edit mode so it can't be exposed accidentally. */}
            <div className="bg-white border-2 border-dashed border-[#F5A623]/60 rounded-lg p-2 space-y-1">
              <div className="flex items-center gap-1 text-[10px] font-bold text-[#a06e10]">
                <KeyRound className="w-3 h-3" />
                كلمة سر جديدة (اختياري — اتركها فارغة بلا تغيير)
              </div>
              <div className="relative">
                <input
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  data-testid={`cust-password-input-${doc.id}`}
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••"
                  autoComplete="new-password"
                  className="w-full px-3 py-1.5 pl-9 rounded-lg border border-[#E4EBE6] text-sm bg-white"
                  dir="ltr"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid={`cust-password-toggle-${doc.id}`}
                  className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[#5C6E64] hover:text-[#1E3A5F] p-0.5"
                  title={showPassword ? "إخفاء" : "إظهار"}
                >
                  {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
            <div className="flex gap-1">
              <button
                onClick={saveText}
                data-testid={`cust-save-${doc.id}`}
                className="flex-1 bg-[#7ED321] text-white py-1.5 rounded-lg text-xs font-bold flex items-center justify-center gap-1"
              >
                <Save className="w-3 h-3" />حفظ
              </button>
              <button
                onClick={() => {
                  setEditing(false);
                  setForm({
                    customer_name: doc.customer_name || "",
                    market_name: doc.market_name || "",
                    address: doc.address || "",
                    customer_id: doc.customer_id || "",
                    phone: doc.phone || "",
                  });
                  setNewPassword("");
                  setShowPassword(false);
                }}
                className="px-3 bg-[#F3F6F4] text-[#5C6E64] py-1.5 rounded-lg text-xs"
              >
                إلغاء
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-1.5">
            {/* Tight pills row — all stacked closely */}
            <div className="flex flex-wrap gap-1.5 text-[11px]">
              <span className="bg-[#3B82F6]/15 text-[#1E3A5F] font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                <Hash className="w-3 h-3" />
                ايباد: <span dir="ltr">{doc.customer_id || "—"}</span>
              </span>
              <span className="bg-[#7ED321]/15 text-[#4a7e15] font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                <Store className="w-3 h-3" />
                {doc.market_name || "بدون ماركت"}
              </span>
              <span className="bg-[#F5A623]/15 text-[#a06e10] font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                <Phone className="w-3 h-3" />
                <span dir="ltr">{doc.phone || "—"}</span>
              </span>
              {doc.address && (
                <span className="bg-[#9F7AEA]/15 text-[#5b3aa6] font-bold px-2.5 py-1 rounded-full flex items-center gap-1 max-w-full">
                  <MapPin className="w-3 h-3" />
                  <span className="truncate max-w-[180px]">{doc.address}</span>
                </span>
              )}
              <span
                className={`px-2.5 py-1 rounded-full font-bold flex items-center gap-1 ${
                  doc.needs_attention
                    ? "bg-[#E85D45]/15 text-[#a83a25]"
                    : "bg-[#22C55E]/15 text-[#1c7c3a]"
                }`}
              >
                آخر طلب:
                <span dir="ltr">
                  {doc.days_since_last_order === 9999
                    ? "لم يطلب"
                    : doc.days_since_last_order === 0
                    ? "اليوم"
                    : `${doc.days_since_last_order} يوم`}
                </span>
              </span>
            </div>
            <button
              onClick={() => setEditing(true)}
              data-testid={`cust-edit-${doc.id}`}
              className="w-full bg-[#3B82F6] hover:bg-[#2563eb] text-white py-1.5 rounded-full text-xs font-bold flex items-center justify-center gap-1"
            >
              <Edit3 className="w-3 h-3" />تعديل البيانات
            </button>
          </div>
        )}
      </motion.div>

      {/* Crop modal */}
      <ImageCropModal
        file={pendingFile?.file || null}
        aspect={1}
        onCancel={() => setPendingFile(null)}
        onConfirm={uploadCropped}
      />

      {/* Lightbox viewer with WhatsApp share */}
      {lightboxSlot && (
        <ImageLightbox
          src={
            doc.documents?.[lightboxSlot.key]
              ? `${API}/files/${doc.documents[lightboxSlot.key]}`
              : null
          }
          label={lightboxSlot.label}
          captionExtra={`${doc.customer_name || ""} ${doc.market_name ? "— " + doc.market_name : ""}`}
          onClose={() => setLightboxSlot(null)}
        />
      )}
    </>
  );
};

const CustomerDocsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterOffline, setFilterOffline] = useState(false);
  const [filterType, setFilterType] = useState(""); // "" = all, "ipad", "regular"

  const fetchData = async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/customer-docs`);
      setData(d);
    } catch (err) {
      toast.error("فشل تحميل وثائق الزبائن");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const { data: d } = await axios.get(`${API}/admin/customer-docs`);
        if (!cancelled) setData(d);
      } catch {
        if (!cancelled) toast.error("فشل تحميل وثائق الزبائن");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    const id = setInterval(run, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const filtered = useMemo(() => {
    const list = data?.customers || [];
    let arr = list;
    if (filterOffline) arr = arr.filter((c) => !c.is_online);
    // Customer-type filter: "ipad" / "regular" / "" (all). When "ipad" is
    // active, also include customers with no tag yet because by default
    // they're treated as iPad customers in accounting.
    if (filterType === "ipad") arr = arr.filter((c) => c.customer_type !== "regular");
    else if (filterType === "regular") arr = arr.filter((c) => c.customer_type === "regular");
    const q = search.trim().toLowerCase();
    if (!q) return arr;
    return arr.filter((c) => {
      return (
        String(c.customer_id || "").toLowerCase().includes(q) ||
        (c.customer_name || "").toLowerCase().includes(q) ||
        (c.market_name || "").toLowerCase().includes(q) ||
        (c.phone || "").toLowerCase().includes(q)
      );
    });
  }, [data, search, filterOffline, filterType]);

  // Counters per type for the top filter pills.
  const ipadCount = useMemo(
    () => (data?.customers || []).filter((c) => c.customer_type !== "regular").length,
    [data]
  );
  const regularCount = useMemo(
    () => (data?.customers || []).filter((c) => c.customer_type === "regular").length,
    [data]
  );
  // Online breakdown by customer type — used to render "ايباد متصل" vs
  // "موبايل متصل" segments in the connectivity counter card.
  const onlineIpadCount = useMemo(
    () => (data?.customers || []).filter((c) => c.is_online && c.customer_type !== "regular").length,
    [data]
  );
  const onlineMobileCount = useMemo(
    () => (data?.customers || []).filter((c) => c.is_online && c.customer_type === "regular").length,
    [data]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-3" data-testid="customer-docs-tab">
      {data.inactive_3days_count > 0 && (
        <div className="bg-gradient-to-l from-[#E85D45] to-[#F5A623] text-white rounded-2xl p-3 flex items-center gap-3 shadow-md">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <p className="text-sm font-bold">
            ملاحظة: <span dir="ltr">{data.inactive_3days_count}</span> زبون لم يطلبوا منذ 3 أيام أو أكثر — يجب على الإدارة اتخاذ الإجراء اللازم.
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => setFilterOffline(false)}
          data-testid="cust-docs-online-card"
          className={`text-right rounded-2xl p-3 transition-all ${
            !filterOffline
              ? "bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white shadow-md ring-2 ring-[#3B82F6]"
              : "bg-white border border-[#E4EBE6] text-[#1E3A5F]"
          }`}
        >
          <div className="flex items-center justify-between">
            <Wifi className="w-5 h-5" />
            <p className="text-xs opacity-80">المتصلون الآن</p>
          </div>
          <p className="font-bold text-2xl mt-1" dir="ltr">{data.online_count}</p>
          {/* Breakdown: how many of the connected users are iPad vs mobile. */}
          {(onlineIpadCount > 0 || onlineMobileCount > 0) && (
            <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
              <span
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold"
                style={{ background: "#15803D", color: "#fff" }}
                data-testid="cust-docs-online-ipad"
                dir="ltr"
              >
                <Tablet className="w-3 h-3" />
                {onlineIpadCount} ايباد
              </span>
              <span
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold"
                style={{ background: "#1D4ED8", color: "#fff" }}
                data-testid="cust-docs-online-mobile"
                dir="ltr"
              >
                <UserCheck className="w-3 h-3" />
                {onlineMobileCount} موبايل
              </span>
            </div>
          )}
        </button>
        <button
          onClick={() => setFilterOffline(true)}
          data-testid="cust-docs-offline-card"
          className={`text-right rounded-2xl p-3 transition-all ${
            filterOffline
              ? "bg-gradient-to-l from-[#5C6E64] to-[#9CA3AF] text-white shadow-md ring-2 ring-[#9CA3AF]"
              : "bg-white border border-[#E4EBE6] text-[#5C6E64]"
          }`}
        >
          <div className="flex items-center justify-between">
            <WifiOff className="w-5 h-5" />
            <p className="text-xs opacity-80">غير المتصلين</p>
          </div>
          <p className="font-bold text-2xl mt-1" dir="ltr">{data.offline_count}</p>
        </button>
      </div>

      {/* Customer-type filter pills — click to focus on iPad customers OR
          on Regular (cash) customers. A second click returns to all. */}
      <div className="flex items-center gap-2" data-testid="cust-docs-type-filter">
        <button
          onClick={() => setFilterType(filterType === "ipad" ? "" : "ipad")}
          data-testid="cust-docs-filter-ipad"
          className={`flex-1 flex items-center justify-center gap-2 rounded-2xl px-3 py-2.5 font-black text-sm border-2 transition-all ${
            filterType === "ipad"
              ? "text-white shadow scale-[1.02]"
              : "bg-white text-[#15803D]"
          }`}
          style={{
            background: filterType === "ipad" ? "linear-gradient(135deg, #15803D 0%, #22C55E 100%)" : undefined,
            borderColor: "#22C55E",
          }}
        >
          <Tablet className="w-4 h-4" />
          ايباد
          <span className="bg-white/25 backdrop-blur rounded-full px-2 text-[10px]" dir="ltr">{ipadCount}</span>
        </button>
        <button
          onClick={() => setFilterType(filterType === "regular" ? "" : "regular")}
          data-testid="cust-docs-filter-regular"
          className={`flex-1 flex items-center justify-center gap-2 rounded-2xl px-3 py-2.5 font-black text-sm border-2 transition-all ${
            filterType === "regular"
              ? "text-white shadow scale-[1.02]"
              : "bg-white text-[#1E3A5F]"
          }`}
          style={{
            background: filterType === "regular" ? "linear-gradient(135deg, #1E3A5F 0%, #1D4ED8 100%)" : undefined,
            borderColor: "#1D4ED8",
          }}
        >
          <UserCheck className="w-4 h-4" />
          زبون
          <span className="bg-white/25 backdrop-blur rounded-full px-2 text-[10px]" dir="ltr">{regularCount}</span>
        </button>
      </div>

      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="cust-docs-search"
          placeholder="ابحث برقم الايباد / اسم الزبون / اسم الماركت / الهاتف..."
          className="w-full pr-9 pl-9 py-2.5 rounded-full bg-white border-2 border-[#3B82F6]/30 text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="absolute left-3 top-1/2 -translate-y-1/2 p-1 hover:bg-[#F3F6F4] rounded-full"
          >
            <X className="w-3.5 h-3.5 text-[#5C6E64]" />
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border-2 border-dashed border-[#BAE6FD]">
          <User className="w-12 h-12 text-[#BAE6FD] mx-auto mb-2" />
          <p className="text-[#5C6E64]">لا يوجد زبائن مطابقين</p>
        </div>
      ) : (
        // 2-column wider cards
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((c) => (
            <CustomerDocCard key={c.id} doc={c} onRefresh={fetchData} />
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomerDocsTab;
